import { NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import {
	CreditBalanceSnapshot,
	CreditBalanceStateService
} from '@brand-lolly/app/billing/services/credit-balance-state.service';
import {
	creditsFromMilli,
	freeAllowanceRenewsLabel
} from '@brand-lolly/app/billing/types/credit-statement.types';
import {
	BalanceComponent,
	planIndicatorType,
	SkeletonLoaderComponent
} from 'components';
import { TeamPermissionsStateService } from '@brand-lolly/app/user-management/team/services/team-permissions-state.service';
import { TeamAction } from '@brand-lolly/app/user-management/team/types/team.types';
import { Subject, takeUntil } from 'rxjs';

/** Where the statement lives, and where the low-balance controls send a brand. */
export const CREDITS_STATEMENT_ROUTE = '/user-management/credits';
/** The upgrade route, as the profile menu and burger menu already use it. */
const UPGRADE_ROUTE = '/user-management/manage-plan';

/**
 * The brand's credit balance in the application header — so it is on screen on
 * every page that can start a metered action, without going into billing to
 * find it.
 *
 * Visual comes from the library's `app-balance`, unchanged: it already draws the
 * coin, the figure, the free pill and the Upgrade affordance this needs. The two
 * branches below exist because `app-balance` renders its own Upgrade button when
 * the account is free or empty — nesting that inside this component's own button
 * would put a control inside a control, so when it shows, it IS the control.
 */
@Component({
	standalone: true,
	selector: 'app-credit-balance-indicator',
	templateUrl: './credit-balance-indicator.component.html',
	styleUrls: ['./credit-balance-indicator.component.scss'],
	imports: [NgIf, MatTooltipModule, BalanceComponent, SkeletonLoaderComponent]
})
export class CreditBalanceIndicatorComponent implements OnInit, OnDestroy {
	/**
	 * True while the first read is in flight. Starts TRUE: `ngOnInit` always
	 * reads, and starting false paints a zero balance before the request goes
	 * out — which for a balance is not a cosmetic flicker, it is a wrong figure.
	 */
	loading = true;
	/**
	 * False until there is a figure worth showing. A failed read keeps this
	 * false: an unreadable balance must not render as an empty one.
	 */
	visible = false;
	credits = 0;
	indicatorType: planIndicatorType = 'pro';
	/** Mirrors `app-balance`'s own condition for drawing its Upgrade button. */
	showUpgrade = false;
	tooltip = '';

	/** What the BALANCE says about offering an upgrade, before the role does. */
	private upgradeOffered = false;
	/**
	 * Whether this person may change what the account pays. A seated member may
	 * not, so they get the plain chip into their own statement — the balance is
	 * theirs to see, the plan is not theirs to change.
	 */
	private canManageBilling = true;

	private unsubscriber = new Subject<void>();

	constructor(
		private creditBalanceState: CreditBalanceStateService,
		private router: Router,
		private permissionsState: TeamPermissionsStateService
	) {}

	ngOnInit(): void {
		this.creditBalanceState.snapshot$
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(snapshot => this.applySnapshot(snapshot));
		this.creditBalanceState.loading$
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(loading => {
				// Only the FIRST read shows a placeholder. A later refresh
				// replaces a real figure in place; blanking it would make the
				// header flicker on every navigation.
				if (!this.visible) {
					this.loading = loading;
				}
			});
		this.permissionsState
			.ensureLoaded()
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(() => {
				this.canManageBilling = this.permissionsState.can(
					TeamAction.BILLING_MANAGE
				);
				this.applyUpgradeVisibility();
			});
		this.creditBalanceState.refresh();
	}

	ngOnDestroy(): void {
		this.unsubscriber.next();
		this.unsubscriber.complete();
	}

	openStatement(): void {
		this.router.navigate([CREDITS_STATEMENT_ROUTE]);
	}

	upgrade(): void {
		this.router.navigate([UPGRADE_ROUTE], {
			queryParams: { openPlans: true }
		});
	}

	private applySnapshot(snapshot: CreditBalanceSnapshot): void {
		if (!snapshot.loaded || snapshot.statement === null) {
			this.visible = false;
			return;
		}
		this.loading = false;
		this.visible = true;
		// The governing figure, not the raw balance: a free ceiling can refuse
		// an action the balance would cover.
		// Floored, never rounded: rounding 0.6 up to 1 would offer a credit the
		// meter does not have, and the tooltip carries the exact figure.
		this.credits = Math.max(0, Math.floor(snapshot.spendableCredits));
		this.indicatorType = snapshot.bounded ? 'free' : 'pro';
		this.upgradeOffered = snapshot.bounded || this.credits === 0;
		this.applyUpgradeVisibility();
		this.tooltip = this.buildTooltip(snapshot);
	}

	/** The balance and the role both have to allow it — either can withhold it. */
	private applyUpgradeVisibility(): void {
		this.showUpgrade = this.upgradeOffered && this.canManageBilling;
	}

	private buildTooltip(snapshot: CreditBalanceSnapshot): string {
		const figure = formatCredits(snapshot.spendableCredits);
		if (snapshot.exhausted) {
			return snapshot.bounded
				? `This month's free credits are used up; they renew on ${freeAllowanceRenewsLabel()}.`
				: 'No credits left to spend. Buy credits to keep running metered actions.';
		}
		if (snapshot.bounded && snapshot.ceilingCredits !== null) {
			return `${figure} of your ${formatCredits(
				snapshot.ceilingCredits
			)} free credits left this month · renews ${freeAllowanceRenewsLabel()}. Open your credit statement.`;
		}
		// The held amount is NAMED, and the balance it is subtracted from is not
		// mentioned at all. The sentence used to print the spendable figure and
		// the balance side by side — 1,232.85 and 1,235.85 — two seven-character
		// numbers differing in one digit, which reads as the same number twice
		// by accident. The one fact the sentence exists to deliver, the 3.00
		// that is held, was the only thing it did not say: it was "the rest",
		// and the reader had to subtract to find it. The balance is recoverable
		// by adding the two, so nothing is lost by leaving it out.
		if (snapshot.statement !== null && snapshot.statement.held_milli > 0) {
			const held = formatCredits(
				creditsFromMilli(snapshot.statement.held_milli)
			);
			const heldNoun = held === '1' ? 'credit is' : 'credits are';
			return `${figure} credits available to spend now. ${held} ${heldNoun} held by work already running. Open your credit statement.`;
		}
		return `${figure} credits available to spend. Open your credit statement.`;
	}
}

/** Whole credits with thousands separators; sub-credit figures keep two places. */
function formatCredits(credits: number): string {
	const fractionDigits = Number.isInteger(credits) ? 0 : 2;
	return credits.toLocaleString('en-US', {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits
	});
}
